import React, { type FC } from 'react'

import { useIntl } from 'react-intl'

import {
  DataListHeader,
  DataListRow,
  Flex,
  Grid,
  GridCell,
  MESSAGE_BOX_VARIANT_HELP,
  MessageBox,
  PaginationBar,
  SKELETON_LOADER_VARIANT_ONWHITE,
  SkeletonLoader,
} from 'mailup-web-components'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import DecoratedText from 'Pages/RecipientsList/components/modals/bulkFindReplace/DecoratedText'

import { modalBulkFindReplaceChangePageCallback } from 'Pages/RecipientsList/recoil/callbacks/modalBulkFindReplace'
import {
  modalBulkFindReplacePreviewResultsState,
  modalBulkFindReplaceState,
} from 'Pages/RecipientsList/recoil/state/modalBulkFindReplace'

const PreviewResults: FC = () => {
  const intl = useIntl()

  const {
    previewStatus,
    totalItems,
    pageNumber,
    pageSize,

    previewFindText,
    previewFindCaseSensitive,
    previewReplaceText,
    previewFindType,
  } = useRecoilValue(modalBulkFindReplaceState)

  const results = useRecoilValue(modalBulkFindReplacePreviewResultsState)

  const changePage = useRecoilCallback(
    modalBulkFindReplaceChangePageCallback,
    [],
  )

  const handleChangePageNumber = (pageNumber: number): void => {
    void changePage({ pageNumber })
  }
  const handleChangePageSize = (pageSize: number): void => {
    void changePage({ pageNumber: 1, pageSize })
  }

  const heads = [
    {
      label: intl.formatMessage({
        id: 'Load.RecipientsList.ModalBulkFindReplacePreviewBefore',
      }),
      span: 6,
    },
    {
      label: intl.formatMessage({
        id: 'Load.RecipientsList.ModalBulkFindReplacePreviewAfter',
      }),
      span: 6,
    },
  ]

  return (
    <section>
      <DataListHeader heads={heads} />
      <section>
        {(previewStatus === 'paging' || previewStatus === 'fetching') &&
          Array.from({ length: pageSize }).map((_, i) => (
            <DataListRow key={`skel_${i}`} padding="8px 0">
              <Grid cols={2}>
                <GridCell span={1}>
                  <SkeletonLoader
                    variant={SKELETON_LOADER_VARIANT_ONWHITE}
                    margin="0 0 4.5px 0"
                  />
                </GridCell>
                <GridCell span={1}>
                  <SkeletonLoader
                    variant={SKELETON_LOADER_VARIANT_ONWHITE}
                    margin="0 0 4.5px 0"
                  />
                </GridCell>
              </Grid>
            </DataListRow>
          ))}
        {previewStatus === 'ready' &&
          results.map((o, i) => (
            <DataListRow key={o.id} padding="8px 0">
              <Grid cols={2}>
                <GridCell span={1}>
                  <DecoratedText
                    content={o.oldValue}
                    findText={previewFindText}
                    findType={previewFindType}
                    findCaseSensitive={previewFindCaseSensitive}
                  />
                </GridCell>
                <GridCell span={1}>
                  <DecoratedText
                    content={o.newValue}
                    findText={previewReplaceText}
                    findType={previewFindType}
                    findCaseSensitive={previewFindCaseSensitive}
                  />
                </GridCell>
              </Grid>
            </DataListRow>
          ))}
      </section>

      {totalItems > 0 && (
        <Flex flexDirection="column" gap="16px">
          <PaginationBar
            margin="16px 0 0 0"
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalItems={totalItems}
            strings={{
              of: intl.formatMessage({ id: 'Load.Pager.Of' }),
              label: intl.formatMessage({ id: 'Load.Pager.Items' }),
            }}
            handleChangePage={handleChangePageNumber}
            handleChangePageSize={handleChangePageSize}
            data-mu-test="modal-find-replace-pagination"
            sizeSelectOpenUp
          />

          <MessageBox variant={MESSAGE_BOX_VARIANT_HELP}>
            {intl.formatMessage(
              {
                id: 'Load.RecipientsList.ModalBulkFindReplacePreviewLimitInfo',
              },
              {
                strong: (chunks) => <strong>{chunks}</strong>,
                N: 100,
              },
            )}
          </MessageBox>
        </Flex>
      )}
    </section>
  )
}

export default PreviewResults
